// Réglages · Bénévolat (D-127, maquettes r1 à r5 ; D-139, maquette n1 : une seule page en rubriques repliables, ouverte
// par la ⚙ de l'onglet Bénévolat, l'onglet restant allumé, retour « ‹ Bénévolat »). Le bureau seul, module Bénévolat
// activé (D-122 : la route est celle du module, src/domain/modules.ts).
//   #/benevoles/reglages[/<rubrique>]  les huit rubriques, chacune son résumé ; la rubrique de l'adresse s'ouvre :
//     besoins       besoins des matchs (D-119, src/ui/besoins.ts)
//     parcours      les parcours : renommer, masquer, créer (en feuille)
//     ranger        les noms de créneaux sans parcours, et ceux déjà rangés
//     recompenses   la récompense de chaque grade (D-100 ; le renvoi « ⚙ Bénévolat › » des réglages des avantages y mène)
//     jeunes        les créneaux ouverts aux mineurs par défaut, nom par nom
//     groupes       groupes de bénévoles : création ouverte, essai, rythme (D-105)
//     annonces      « Bravo à… » du lundi, Assiduité, l'heure du rappel de la veille
//     recus         Frais et reçus fiscaux : un renvoi vers les réglages des reçus (D-124)
// Les anciennes adresses (#/club/benevolat[/…], #/benevoles/matchs, #/benevoles/parcours) y mènent (src/domain/nav.ts).
// Les règles sont en base (migrations 20261103100000 et 20261103110000) ; les résumés, dans src/domain/reglages.ts.
import './reglages.css';
import { badgeLabels, type BadgeLabel } from '../lib/api';
import { needsBoard } from '../lib/besoins-api';
import {
  deleteParcours, ensureParcours, gradeRewards, hideParcours, rangeLabel, saveParcours, saveVolunteerSettings, setYouthLabel, volunteerSettings,
  youthLabels, type RewardRow, type VolunteerSettings, type YouthLabel,
} from '../lib/reglages-api';
import { GRADES, labelKey, parcoursOf, settingsParcours, slotParcours, type GradeKey, type GradeReward, type ParcoursDef } from '../domain/badges';
import {
  EVERY_CHOICES, groupsSummary, needsSummary, parcoursSummary, relancesSummary, REMINDER_CHOICES, rewardsSummary, RUBRIC_TITLE,
  toRangeChip, TRIAL_CHOICES, youthSummary, type Rubric,
} from '../domain/reglages';
import { modOn, type Ctx } from './context';
import { $, $$, armed, html, mount, openSheet, toast, type Html } from './dom';
import { renvoi, settingsPage } from './nav';
import { gchip, medal, openReward, playersWord } from './badges';
import { picto } from './perks';
import { renderMatchNeeds } from './besoins';

/** L'écran des frais et reçus fiscaux des bénévoles (D-124, renderReceiptsBoard) : la rubrique 8 y mène. */
export const RECUS_ROUTE: string | null = '#/club/recus';   // les réglages des reçus (B8)
const recusSub = 'Barème kilométrique, intérêt général, RNA ou SIREN';

const isAdmin = (ctx: Ctx) => !!ctx.club && ctx.adminOf.some(c => c.id === ctx.club!.id);

/** L'introduction d'une rubrique, en tête de son contenu. */
const intro = (t: string | Html): Html => html`<p class="muted small nv-intro">${t}</p>`;

/** « Table de marque, Chronomètre, 24 secondes » (quatre noms au plus). */
const namesLine = (names: string[], max = 4) => (names.length > max ? `${names.slice(0, max).join(', ')}…` : names.join(', '));

// -----------------------------------------------------------------------------
// Réglages · Bénévolat : les huit rubriques sur une page (maquette n1)
// -----------------------------------------------------------------------------
export async function renderVolunteerSettings(root: HTMLElement, ctx: Ctx, open?: string): Promise<void> {
  const club = ctx.club;
  if (!club || !isAdmin(ctx)) {
    mount(root, html`<section class="screen"><a class="back" href="#/benevoles">‹ Bénévolat</a><div class="empty"><p class="empty-title">Réservé au bureau du club.</p></div></section>`);
    return;
  }
  await ensureParcours(club.id);
  const [board, labels, rewards, youth, s] = await Promise.all([
    needsBoard(club.id).catch(() => null),
    badgeLabels(club.id).catch(() => [] as BadgeLabel[]),
    gradeRewards(club.id).catch(() => [] as RewardRow[]),
    youthLabels(club.id).catch(() => [] as YouthLabel[]),
    volunteerSettings(club.id),
  ]);
  const unitsWithRules = new Set((board?.rules ?? []).map(r => `${r.team_id}|${r.squad_id ?? ''}`)).size;
  const unknown = labels.filter(l => l.parcours === 'coup_de_main').length;
  const again = () => renderVolunteerSettings(root, ctx);
  const rub = (key: Rubric, sum: string, body: (el: HTMLElement) => void | Promise<void>, chip?: Html) =>
    ({ key, num: RUBRICS_SHOWN.indexOf(key) + 1, title: RUBRIC_TITLE[key], sum, chip, guide: `rg-${key}`, body });
  settingsPage(root, {
    key: 'benevolat', open: open ?? null, guide: 'rg-list',
    intro: 'Tout ce qui se règle pour les bénévoles, sur une seule page : touche une rubrique pour l’ouvrir.',
    rubrics: [
      rub('besoins', needsSummary(board?.roles ?? [], unitsWithRules), el => renderMatchNeeds(el, ctx)),
      rub('parcours', parcoursSummary(settingsParcours()), el => parcoursBody(el, ctx, labels, again)),
      rub('ranger', 'Les noms de créneaux sans parcours', el => rangeBody(el, ctx, labels),
        unknown ? html`<span class="rg-tags"><span class="chip chip-accent">${toRangeChip(unknown)}</span></span>` : undefined),
      rub('recompenses', modOn(ctx, 'avantages') ? rewardsSummary(rewards) : 'Avec le module Avantages : rien n’est promis sans lui', el => rewardsBody(el, ctx, rewards, again)),
      rub('jeunes', youthSummary(youth), el => youthBody(el, ctx, youth)),
      rub('groupes', groupsSummary(s), el => groupsBody(el, ctx, s)),
      rub('annonces', relancesSummary(s), el => relancesBody(el, ctx, s)),
      ...(RECUS_ROUTE ? [rub('recus', 'Réglés avec les reçus fiscaux : barème, intérêt général, RNA',
        el => mount(el, html`${intro(recusSub)}${renvoi('Barème kilométrique et reçus', 'Reçus fiscaux', RECUS_ROUTE!, 'rg-recus-go')}`))] : []),
    ],
  }, again);
}

/** Les rubriques affichées, dans l'ordre de la maquette r1 (la 8e : les reçus, quand leur écran est là). */
const RUBRICS_SHOWN: Rubric[] = ['besoins', 'parcours', 'ranger', 'recompenses', 'jeunes', 'groupes', 'annonces', 'recus'];

// -----------------------------------------------------------------------------
// 2. Parcours (maquettes r2, r2b, r3)
// -----------------------------------------------------------------------------
/** Les pictos proposés pour un parcours créé (ceux de l'app, src/domain/pictos.ts). */
const PICKS = ['photo', 'communication', 'coeur', 'tournoi', 'cadeau', 'accueil', 'ballon', 'etoile', 'caisse', 'ticket', 'gateaux', 'rangement'];

function parcoursBody(root: HTMLElement, ctx: Ctx, labels: BadgeLabel[], reload: () => void): void {
  const club = ctx.club!;
  const defs = settingsParcours();
  const names = (k: string) => labels.filter(l => l.parcours === k).map(l => l.label);
  const sub = (d: ParcoursDef) => (d.hidden ? 'Masqué : il ne s’affiche plus, les étoiles déjà gagnées restent'
    : names(d.key).length ? namesLine(names(d.key)) : d.blurb);
  mount(root, html`
      ${intro('Un parcours par type de mission. Renomme, masque, ou crée les tiens ; chacun dit quels créneaux comptent.')}
      <ul class="rg-list" data-guide="rg-parcours-list">${defs.map(d => html`
        <li class="rg-pc ${d.hidden ? 'off' : ''}">
          <button type="button" class="rg-pc-main" data-edit="${d.key}" aria-label="Régler le parcours ${d.name}">
            ${medal(d.key, d.hidden ? 'off' : 'bronze', 44)}
            <span class="card-main"><strong>${d.name}</strong><span class="muted small">${sub(d)}</span>
              ${d.custom ? html`<span class="rg-tags"><span class="chip chip-accent">Créé par le club</span></span>`
                : d.name !== d.origin ? html`<span class="rg-tags"><span class="chip chip-soft">Renommé · ${d.origin}</span></span>` : ''}</span>
          </button>
          <button type="button" class="iv-switch" role="switch" aria-checked="${String(!d.hidden)}" data-hide="${d.key}"
            aria-label="${d.hidden ? `Afficher le parcours ${d.name}` : `Masquer le parcours ${d.name}`}"></button>
        </li>`)}</ul>
      <button type="button" class="btn btn-ghost rg-create" id="rg-create" data-guide="rg-create">＋ Créer un parcours</button>
      <p class="hint">Même échelle pour tous : En herbe dès la 1re mission, Bronze dès 5, Argent 15, Or 30, Légende 50. Assiduité (${playersWord(ctx)}) se règle dans « Annonces et relances ».</p>`);

  for (const b of $$<HTMLButtonElement>(root, '[data-edit]')) {
    b.addEventListener('click', () => openParcours(ctx, parcoursOf(b.dataset.edit!), labels, reload));
  }
  for (const b of $$<HTMLButtonElement>(root, '[data-hide]')) {
    b.addEventListener('click', async () => {
      const k = b.dataset.hide!, d = parcoursOf(k), hide = b.getAttribute('aria-checked') === 'true';
      b.disabled = true;
      try {
        await hideParcours(club.id, k, hide);
        toast(hide ? `« ${d.name} » masqué : ses étoiles restent` : `« ${d.name} » de nouveau affiché`);
        reload();
      } catch { b.disabled = false; toast('Impossible pour le moment.', 'ko'); }
    });
  }
  $(root, '#rg-create')!.addEventListener('click', () => openParcours(ctx, null, labels, reload));
}

/** Créer un parcours (maquette r3) ou en régler un. Un parcours de base : son nom seulement (ses créneaux se rangent dans
 *  « Ranger les créneaux ») ; un parcours créé : nom, picto, créneaux qui comptent, une phrase. */
function openParcours(ctx: Ctx, d: ParcoursDef | null, labels: BadgeLabel[], onDone: () => void): void {
  const club = ctx.club!;
  const custom = !d || d.custom;
  const mine = d ? labels.filter(l => l.parcours === d.key).map(l => l.label) : [];
  if (!custom) {
    openSheet(`Le parcours ${d!.name}`, html`
      <form class="form" id="rg-f-base" novalidate>
        <label class="field"><span>Nom</span><input name="name" maxlength="40" value="${d!.name}" placeholder="${d!.origin}"></label>
        <p class="hint">Son nom d’origine : ${d!.origin}. Vide, il le reprend. Les étoiles ne bougent pas.</p>
        <div class="field"><span>Les créneaux qui comptent</span>
          ${mine.length ? html`<div class="chip-toggles">${mine.map(l => html`<span class="chip chip-mute">${l}</span>`)}</div>` : html`<p class="muted small">${d!.blurb}</p>`}
          <a class="btn-link-accent" href="#/benevoles/reglages/ranger" data-replace>Ranger les créneaux ›</a></div>
        <p class="form-msg" role="alert"></p>
        <button class="btn btn-primary" type="submit">Enregistrer</button>
      </form>`, (sheet, close) => {
      const f = $<HTMLFormElement>(sheet, '#rg-f-base')!;
      f.addEventListener('submit', async (e) => {
        e.preventDefault();
        const name = String(new FormData(f).get('name') ?? '').trim();
        const btn = $<HTMLButtonElement>(f, 'button[type=submit]')!;
        btn.disabled = true;
        try { await saveParcours(club.id, { key: d!.key, name, picto: null, blurb: null, labels: null }); close(); toast(`Parcours « ${name || d!.origin} » enregistré`); onDone(); }
        catch (err) { btn.disabled = false; $(f, '.form-msg')!.textContent = problem(err); }
      });
    });
    return;
  }
  // Un parcours créé : les noms proposés sont les siens, puis ceux qui n'ont pas de parcours (« Coup de main »).
  const byKey = new Map<string, string>();
  for (const l of [...mine, ...labels.filter(x => x.parcours === 'coup_de_main').map(x => x.label)]) if (!byKey.has(labelKey(l))) byKey.set(labelKey(l), l);
  const chosen = new Set(mine.map(labelKey));
  let pic = d?.picto ?? PICKS[0]!;
  const chips = () => html`${[...byKey].map(([k, l]) => html`
    <button type="button" class="chip-toggle" data-lab="${k}" aria-pressed="${String(chosen.has(k))}">${l}</button>`)}
    <button type="button" class="chip-toggle rg-add-b" id="rg-add-b" aria-expanded="false">＋ Un nom de créneau</button>`;
  openSheet(d ? `Le parcours ${d.name}` : 'Créer un parcours', html`
    <form class="form" id="rg-f-pc" novalidate>
      <label class="field"><span>Nom</span><input name="name" maxlength="40" value="${d?.name ?? ''}" placeholder="ex. Photo et vidéo" data-guide="rg-pc-name"></label>
      <div class="field"><span>Picto</span><div class="rg-picks" role="radiogroup" aria-label="Picto">${PICKS.map(k => html`
        <button type="button" role="radio" data-pic="${k}" aria-pressed="${String(k === pic)}" aria-label="${k}">${picto(k, 22)}</button>`)}</div></div>
      <div class="field" data-guide="rg-pc-labels"><span>Les créneaux qui comptent</span>
        <div class="chip-toggles" id="rg-labs">${chips()}</div>
        <div class="rg-add" id="rg-add" hidden>
          <input id="rg-add-in" maxlength="60" list="rg-all-labels" placeholder="ex. Vidéo du match" aria-label="Un nom de créneau">
          <button type="button" class="btn btn-ghost btn-sm" id="rg-add-ok">Ajouter</button></div>
        <datalist id="rg-all-labels">${labels.map(l => html`<option value="${l.label}"></option>`)}</datalist></div>
      <label class="field"><span>En une phrase (vu des bénévoles)</span><input name="blurb" maxlength="140" value="${d?.blurb ?? ''}" placeholder="ex. Photos et vidéos des matchs pour le club et les familles"></label>
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit" data-guide="rg-pc-save">${d ? 'Enregistrer' : 'Créer le parcours'}</button>
      <p class="hint">Les créneaux déjà tenus sous ces noms comptent tout de suite : leurs étoiles passent dans ce parcours.</p>
      ${d ? html`<button type="button" class="btn btn-link btn-sm" id="rg-del">Retirer ce parcours</button>` : ''}
    </form>`, (sheet, close) => {
    const f = $<HTMLFormElement>(sheet, '#rg-f-pc')!;
    const msg = (t: string) => { $(f, '.form-msg')!.textContent = t; };
    const wire = () => {
      for (const b of $$<HTMLButtonElement>(f, '[data-lab]')) b.addEventListener('click', () => {
        const k = b.dataset.lab!;
        if (chosen.has(k)) chosen.delete(k); else chosen.add(k);
        b.setAttribute('aria-pressed', String(chosen.has(k)));
      });
      $(f, '#rg-add-b')!.addEventListener('click', () => {
        const box = $(f, '#rg-add')!;
        box.hidden = !box.hidden;
        $(f, '#rg-add-b')!.setAttribute('aria-expanded', String(!box.hidden));
        if (!box.hidden) $<HTMLInputElement>(f, '#rg-add-in')!.focus();
      });
    };
    wire();
    const add = () => {
      const inp = $<HTMLInputElement>(f, '#rg-add-in')!;
      const l = inp.value.trim(), k = labelKey(l);
      if (!k) return;
      if (!byKey.has(k)) byKey.set(k, l);
      chosen.add(k);
      inp.value = '';
      mount($(f, '#rg-labs')!, chips());
      wire();
    };
    $(f, '#rg-add-ok')!.addEventListener('click', add);
    $<HTMLInputElement>(f, '#rg-add-in')!.addEventListener('keydown', e => { if (e.key === 'Enter') { e.preventDefault(); add(); } });
    for (const b of $$<HTMLButtonElement>(f, '[data-pic]')) b.addEventListener('click', () => {
      pic = b.dataset.pic!;
      $$(f, '[data-pic]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
    });
    f.addEventListener('submit', async (e) => {
      e.preventDefault();
      const data = new FormData(f);
      const name = String(data.get('name') ?? '').trim();
      if (!name) { msg('Donne un nom au parcours.'); return; }
      const btn = $<HTMLButtonElement>(f, 'button[type=submit]')!;
      btn.disabled = true;
      try {
        await saveParcours(club.id, { key: d?.key ?? null, name, picto: pic, blurb: String(data.get('blurb') ?? '').trim() || null,
          labels: [...chosen].map(k => byKey.get(k) ?? k) });
        close();
        toast(d ? `Parcours « ${name} » enregistré` : `Parcours « ${name} » créé : les créneaux déjà tenus y comptent`);
        onDone();
      } catch (err) { btn.disabled = false; msg(problem(err)); }
    });
    $<HTMLButtonElement>(f, '#rg-del')?.addEventListener('click', async (e) => {
      const b = e.currentTarget as HTMLButtonElement;
      if (!armed(b, 'Appuie encore pour le retirer')) return;
      b.disabled = true;
      try { await deleteParcours(club.id, d!.key); close(); toast(`Parcours « ${d!.name} » retiré`); onDone(); }
      catch (err) { b.disabled = false; msg(problem(err)); }
    });
  });
}

/** Les refus de la base, dits simplement. */
function problem(e: unknown): string {
  const m = String((e as Error)?.message ?? ''), code = (e as { code?: string })?.code;
  if (code === '23505' || m.includes('déjà pris')) return 'Ce nom est déjà celui d’un autre parcours.';
  if (code === '54000' || m.includes('douze')) return 'Douze parcours créés au plus : masque ceux qui ne servent plus.';
  if (m.includes('missions y comptent')) return 'Des missions y comptent déjà : masque-le plutôt (ses étoiles restent).';
  if (m.includes('nom et picto')) return 'Un nom et un picto, pour le reconnaître.';
  return 'Enregistrement impossible. Réessaie.';
}

// -----------------------------------------------------------------------------
// 3. Ranger les créneaux (maquette r4)
// -----------------------------------------------------------------------------
function rangeBody(root: HTMLElement, ctx: Ctx, labels: BadgeLabel[]): void {
  const club = ctx.club!;
  const unknown = labels.filter(l => l.parcours === 'coup_de_main');
  const known = labels.filter(l => l.parcours !== 'coup_de_main');
  const choices = ['coup_de_main', ...slotParcours().filter(k => k !== 'coup_de_main')];
  const state = (l: BadgeLabel) => (l.parcours === 'coup_de_main' ? 'compte en Coup de main' : `compte en ${parcoursOf(l.parcours).name}`);
  const row = (l: BadgeLabel) => html`
    <li class="rg-tri"><span class="card-main"><strong>« ${l.label} »</strong><span class="muted small" data-st>${state(l)}</span></span>
      <select data-range="${l.label}" data-auto="${l.auto}" aria-label="Le parcours de « ${l.label} »">${[...new Set([...choices, l.parcours])].map(k => html`
        <option value="${k}" ${k === l.parcours ? 'selected' : ''}>${parcoursOf(k).name}</option>`)}</select></li>`;
  // « Déjà rangés » : « Table de marque, Chronomètre → Table de marque · Buvette, Gâteaux → Buvette et cuisine… »
  const groups = choices.filter(k => k !== 'coup_de_main').map(k => ({ k, items: known.filter(l => l.parcours === k) })).filter(x => x.items.length);
  const line = groups.slice(0, 3).map(x => `${namesLine(x.items.map(l => l.label), 3)} → ${parcoursOf(x.k).name}`).join(' · ') + (groups.length > 3 ? '…' : '');
  mount(root, html`
      ${intro('Un nom de créneau que l’app ne reconnaît pas compte en « Coup de main ». Choisis son parcours : les étoiles suivent.')}
      ${unknown.length ? html`<ul class="rg-list" data-guide="rg-unknown">${unknown.map(row)}</ul>`
        : html`<p class="rg-ok">Tous les noms de créneaux ont leur parcours.</p>`}
      ${known.length ? html`
        <section class="block" id="rg-known">
          <div class="block-head"><h3>Déjà rangés</h3><span class="muted small">${known.length} ${known.length > 1 ? 'noms' : 'nom'}</span></div>
          <p class="muted small">${line}</p>
          <button type="button" class="btn btn-ghost btn-sm" id="rg-all" aria-expanded="false">Tout voir</button>
          <ul class="rg-list" id="rg-all-list" hidden>${known.map(row)}</ul>
        </section>` : ''}`);
  $(root, '#rg-all')?.addEventListener('click', (e) => {
    const b = e.currentTarget as HTMLButtonElement, ul = $(root, '#rg-all-list')!;
    ul.hidden = !ul.hidden;
    b.setAttribute('aria-expanded', String(!ul.hidden));
    b.textContent = ul.hidden ? 'Tout voir' : 'Replier';
  });
  for (const s of $$<HTMLSelectElement>(root, '[data-range]')) {
    s.addEventListener('change', async () => {
      const label = s.dataset.range!, k = s.value;
      s.disabled = true;
      try {
        await rangeLabel(club.id, label, k === s.dataset.auto ? null : k);
        s.closest('li')!.querySelector('[data-st]')!.textContent = k === 'coup_de_main' ? 'compte en Coup de main' : `rangé en ${parcoursOf(k).name} ✓`;
        toast(`« ${label} » : ${parcoursOf(k).name}. Les étoiles suivent.`);
      } catch { toast('Impossible pour le moment.', 'ko'); }
      s.disabled = false;
    });
  }
}

// -----------------------------------------------------------------------------
// 4. Récompenses des grades (D-100 : le réglage du bureau, déplacé de « Parcours et récompenses »)
// -----------------------------------------------------------------------------
function rewardsBody(root: HTMLElement, ctx: Ctx, rewards: RewardRow[], reload: () => void): void {
  root.classList.add('gr');
  mount(root, html`
      ${intro('Chaque grade donne son cadeau, une fois par parcours, utilisé avec « Utiliser » comme tout avantage.')}
      ${modOn(ctx, 'avantages') ? html`<section class="block" data-guide="bg-rewards">
        <ul class="gr-rws">${GRADES.map(g => {
          const r = rewards.find(x => x.grade === g.key);
          return html`<li class="gr-rw">${gchip(g.key)}<span class="gr-rw-t">${picto(r?.picto ?? 'cadeau', 16)}<span>${r?.title ?? '—'}${r?.parcours?.length ? html`<small class="muted">${r.parcours.map(p => parcoursOf(p).name).join(', ')}</small>` : ''}</span></span>
            <button type="button" class="link-edit" data-rw="${g.key}">Modifier</button></li>`;
        })}</ul>
        <p class="hint">« Juste un merci dans l’app » : rien à utiliser chez un commerçant. Une récompense peut se réserver à certains parcours.</p>
      </section>` : html`<section class="block"><p class="muted small">Les récompenses viennent avec le module Avantages (Club › Modules et réglages) : rien n’est promis aux bénévoles sans lui.</p></section>`}`);
  for (const b of $$<HTMLButtonElement>(root, '[data-rw]')) {
    b.addEventListener('click', () => openReward(ctx, (rewards.find(r => r.grade === b.dataset.rw) as GradeReward | undefined) ?? null, b.dataset.rw as GradeKey, reload));
  }
}

// -----------------------------------------------------------------------------
// 5. Jeunes (maquette r5) : les créneaux ouverts aux mineurs par défaut, nom par nom
// -----------------------------------------------------------------------------
function youthBody(root: HTMLElement, ctx: Ctx, list: YouthLabel[]): void {
  const club = ctx.club!;
  const on = (l: YouthLabel) => (l.club ? l.open : l.auto);
  mount(root, html`
      ${intro('Les créneaux que les jeunes (moins de 18 ans) voient et peuvent prendre. Par défaut pour chaque nouveau créneau ; un créneau se change aussi un par un.')}
      ${list.length ? html`<ul class="rg-list" data-guide="rg-youth">${list.map(l => html`
        <li class="rg-sw"><span class="card-main"><strong>${l.label}</strong>${!l.club && l.auto ? html`<span class="muted small" data-sub>Aux matchs des équipes de jeunes (U7 à U20)</span>` : ''}</span>
          <button type="button" class="iv-switch" role="switch" aria-checked="${String(on(l))}" data-youth="${l.label}" aria-label="${l.label} : ouvert aux jeunes"></button></li>`)}</ul>`
        : html`<p class="muted">Les noms de créneaux apparaîtront ici dès les premiers besoins des matchs ou le premier appel.</p>`}
      <p class="hint">Un mineur ne voit et ne prend que ces créneaux ; ses parents sont prévenus quand il en prend un. Un seul créneau : « Ouvrir aux jeunes » sur sa carte, onglet Bénévolat.</p>`);
  for (const b of $$<HTMLButtonElement>(root, '[data-youth]')) {
    b.addEventListener('click', async () => {
      const open = b.getAttribute('aria-checked') !== 'true', label = b.dataset.youth!;
      b.disabled = true;
      try {
        await setYouthLabel(club.id, label, open);
        b.setAttribute('aria-checked', String(open));
        b.closest('li')!.querySelector('[data-sub]')?.remove();
        toast(open ? `« ${label} » : ouvert aux jeunes pour les prochains créneaux` : `« ${label} » : réservé aux adultes pour les prochains créneaux`);
      } catch { toast('Impossible pour le moment.', 'ko'); }
      b.disabled = false;
    });
  }
}

// -----------------------------------------------------------------------------
// 6. Groupes de bénévoles et 7. Annonces et relances : des interrupteurs (recos validées)
// -----------------------------------------------------------------------------
const days = (n: number) => `${n} jours`;
const select = (key: keyof VolunteerSettings, value: number | null, choices: (number | null)[], word: (n: number | null) => string, label: string) => html`
  <select class="rg-sel" data-set="${key}" aria-label="${label}">${choices.map(c => html`<option value="${c ?? ''}" ${c === value ? 'selected' : ''}>${word(c)}</option>`)}</select>`;
const swRow = (key: keyof VolunteerSettings, on: boolean, title: string, sub: string) => html`
  <li class="rg-sw"><span class="card-main"><strong>${title}</strong><span class="muted small">${sub}</span></span>
    <button type="button" class="iv-switch" role="switch" aria-checked="${String(on)}" data-sw="${key}" aria-label="${title}"></button></li>`;
const selRow = (title: string, sub: string, control: Html) => html`
  <li class="rg-sw"><span class="card-main"><strong>${title}</strong><span class="muted small">${sub}</span></span>${control}</li>`;

/** Les gestes des interrupteurs et des listes : chacun s'enregistre seul. */
function wireSettings(root: HTMLElement, clubId: string, said: (k: keyof VolunteerSettings, v: boolean | number | null) => string): void {
  for (const b of $$<HTMLButtonElement>(root, '[data-sw]')) {
    b.addEventListener('click', async () => {
      const k = b.dataset.sw as keyof VolunteerSettings, v = b.getAttribute('aria-checked') !== 'true';
      b.disabled = true;
      try { await saveVolunteerSettings(clubId, { [k]: v }); b.setAttribute('aria-checked', String(v)); toast(said(k, v)); }
      catch { toast('Impossible pour le moment.', 'ko'); }
      b.disabled = false;
    });
  }
  for (const s of $$<HTMLSelectElement>(root, '[data-set]')) {
    s.addEventListener('change', async () => {
      const k = s.dataset.set as keyof VolunteerSettings, v = s.value === '' ? null : Number(s.value);
      s.disabled = true;
      try { await saveVolunteerSettings(clubId, { [k]: v }); toast(said(k, v)); }
      catch { toast('Impossible pour le moment.', 'ko'); }
      s.disabled = false;
    });
  }
}

function groupsBody(root: HTMLElement, ctx: Ctx, s: VolunteerSettings): void {
  const club = ctx.club!;
  mount(root, html`
      ${intro('Les groupes donnent envie d’agir et d’y rester : qui les crée, le temps de l’essai, le rythme des missions.')}
      <ul class="rg-list" data-guide="rg-groups">
        ${swRow('group_create_open', s.group_create_open, 'Création ouverte', 'Tout bénévole adulte crée un groupe et en est le chef. Sinon, le bureau seul.')}
        ${selRow('Durée de l’essai', 'Une mission du groupe dans ce délai valide une place ; un groupe créé a le même temps pour se lancer.',
          select('group_trial_days', s.group_trial_days, [...new Set([...TRIAL_CHOICES, s.group_trial_days])].sort((a, b) => a - b), n => days(n!), 'Durée de l’essai'))}
        ${selRow('Une mission tous les', 'Pour garder sa place dans le groupe. Juillet et août ne comptent pas.',
          select('group_every_days', s.group_every_days, [...new Set([...EVERY_CHOICES, s.group_every_days])].sort((a, b) => a - b), n => days(n!), 'Rythme des missions'))}
      </ul>
      <p class="hint">Les nouveaux délais valent pour les décomptes en cours. Les groupes, leurs objectifs et le challenge : onglet Bénévolat, Groupes.</p>
      <a class="btn-link-accent" href="#/benevoles/equipe">Les groupes du club ›</a>`);
  wireSettings(root, club.id, (k, v) => k === 'group_create_open' ? (v ? 'Création ouverte à tout bénévole adulte' : 'Le bureau crée les groupes')
    : k === 'group_trial_days' ? `Essai : ${v} jours` : `Une mission tous les ${v} jours`);
}

function relancesBody(root: HTMLElement, ctx: Ctx, s: VolunteerSettings): void {
  const club = ctx.club!;
  const hours = [...new Set([...REMINDER_CHOICES, ...(s.volunteer_reminder_hour != null ? [s.volunteer_reminder_hour] : [])])].sort((a, b) => a - b);
  mount(root, html`
      ${intro('Ce que l’app envoie toute seule aux bénévoles, et quand.')}
      <ul class="rg-list" data-guide="rg-relances">
        ${swRow('badges_bravo', s.badges_bravo, 'Annonce « Bravo à… » chaque lundi', 'Les nouveaux grades de la semaine, envoyés tout seuls. Chacun peut ne pas y apparaître.')}
        ${swRow('badges_assiduite', s.badges_assiduite, `Parcours Assiduité pour ${playersWord(ctx)}`, 'Une semaine à tous les entraînements = 1 mission. Calculé avec l’appel du coach.')}
        ${selRow('Rappel la veille d’un créneau', 'Aux inscrits, à partir de cette heure (heure du club).',
          select('volunteer_reminder_hour', s.volunteer_reminder_hour, [...hours, null], n => (n == null ? 'Pas de rappel' : `${n} h`), 'Heure du rappel de la veille'))}
      </ul>
      <p class="hint">« Relancer » un créneau à pourvoir reste un geste de l’onglet Bénévolat : le message est prêt, tu l’envoies.</p>`);
  wireSettings(root, club.id, (k, v) => k === 'badges_bravo' ? (v ? 'L’annonce partira lundi' : 'Plus d’annonce le lundi')
    : k === 'badges_assiduite' ? (v ? 'Parcours Assiduité ouvert' : 'Parcours Assiduité coupé')
    : v == null ? 'Plus de rappel la veille' : `Rappel la veille à ${v} h`);
}
